import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, tap } from 'rxjs';

import { environment } from '@environments/environment';
import { IDataEnitas } from '../interfaces/etnias.interface';
import { IDataReligion } from '../interfaces/religion.interface';
import { IdataGrupoSanguineo } from '../interfaces/grupo-sanguineo.interface';
import { IdataOcupaciones } from '../interfaces/ocupacion.interface';
import { IdataEstadoCivil } from '../interfaces/estado-civil.interface';
import { IdataGenero } from '../interfaces/genero.interface';
import { IdataRegiones } from '../interfaces/regiones.interface';
import { IdataPaises } from '../interfaces/paises.interface';
import { UtilServiceService } from "./util-service.service";
import { IndexdbService } from '../../core/database/indexdb.service';

@Injectable({
  providedIn: 'root',
})
export class ServiceFormIdentificacionService {
  private apiUrl = `${environment.baseURL}datos-personales`;
  private readonly utils: UtilServiceService = inject(UtilServiceService);
  private readonly indexDbService: IndexdbService = inject(IndexdbService);

  constructor(private httpClient: HttpClient) { }

  getDatosPersonales(): Observable<any> {
    return new Observable<any>(observer => {
      this.indexDbService.getItem('datos-personales').subscribe(result => {
        if (result) {
          const data = result as any;
          const json = typeof data === 'object' && data.value ? JSON.parse(data.value) : null;
          observer.next(json);
          observer.complete();
        } else {
          this.httpClient.get<any>(this.apiUrl).pipe(
            tap((response) => {
              if (response.data) {
                const key = 'nombre';
                const text = 'NO INFORMADO';
                const { religiones, etnias, gradosInstruccion, ...rest } = response.data;
                response.data = {
                  ...rest,
                  religiones: this.utils.reorderByPriority(religiones, key, text) as IDataReligion[],
                  etnias: this.utils.reorderByPriority(etnias, key, text) as IDataEnitas[],
                  gradosInstruccion: this.utils.reorderByPriority(gradosInstruccion, key, text) as IdataOcupaciones[],
                };
              }
              this.indexDbService.setItem('datos-personales', response).subscribe();
            })
          ).subscribe({
            next: (response) => {
              observer.next(response);
              observer.complete();
            },
            error: (err) => observer.error(err)
          });;
        }
      });
    });
  }

  getEtnias(): Observable<IDataEnitas[]> {
    return this.indexDbService.getDatosPersonales().pipe(
      map(result => result?.etnias ?? [])
    );
  }

  getReligion(): Observable<IDataReligion[]> {
    return this.indexDbService.getDatosPersonales().pipe(
      map(result => result?.religiones ?? [])
    );
  }

  getGrupoSanguineo(): Observable<IdataGrupoSanguineo[]> {
    return this.indexDbService.getDatosPersonales().pipe(
      map(result => result?.gruposSanguineos ?? [])
    );
  }

  getInstrucciones(): Observable<IdataOcupaciones[]> {
    return this.indexDbService.getDatosPersonales().pipe(
      map(result => result?.gradosInstruccion ?? [])
    );
  }

  getEstadoCivil(): Observable<IdataEstadoCivil[]> {
    return this.indexDbService.getDatosPersonales().pipe(
      map(result => result?.estadosCiviles ?? [])
    );
  }

  getGenero(): Observable<IdataGenero[]> {
    return this.indexDbService.getDatosPersonales().pipe(
      map(result => result?.sexos ?? [])
    );
  }

  getRegiones(): Observable<IdataRegiones[]> {
    return this.indexDbService.getDatosPersonales().pipe(
      map(result => result?.regiones ?? [])
    );
  }

  getPaises(): Observable<IdataPaises[]> {
    return this.indexDbService.getDatosPersonales().pipe(
      map(result => result?.paises ?? [])
    );
  }
}
